import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type HeroProps = {
  language: Language;
};
const content = {
  ua: {
    headline: 'CHAMALEON',
    subheadline: 'Преміальний бренд покриттів та автохімії',
    description: '60 років досвіду. Присутні у 80 країнах. Професійні рішення для дистрибуції, ритейлу та кузовних сервісів.',
    cta1: 'Переглянути продукцію',
    cta2: 'Стати партнером'
  },
  en: {
    headline: 'CHAMALEON',
    subheadline: 'Premium Coatings & Auto Chemistry Brand',
    description: '60 years of experience. Present in 80 countries. Professional solutions for distribution, retail, and body shops.',
    cta1: 'View Products',
    cta2: 'Become a Partner'
  }
};
// Abstract visual component
function AbstractVisual() {
  return <div className="relative w-full max-w-sm mx-auto" style={{
    aspectRatio: '1/1'
  }} data-id="element-148">
      {/* Main visual container */}
      <motion.div className="absolute inset-0 rounded-3xl overflow-hidden" animate={{
      rotate: [0, 5, 0]
    }} transition={{
      duration: 20,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-149">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-3xl border border-white/10" data-id="element-150" />
        <div className="absolute inset-0 bg-gradient-to-tr from-chamaleon-orange/10 via-transparent to-transparent" data-id="element-151" />
        <div className="absolute top-1/4 right-1/4 w-32 h-32 bg-chamaleon-orange/20 rounded-full blur-3xl" data-id="element-152" />
      </motion.div>

      {/* Floating geometric elements */}
      <motion.div className="absolute top-1/4 -left-4 sm:-left-8 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-chamaleon-orange/20 to-transparent backdrop-blur-xl border border-chamaleon-orange/20" animate={{
      y: [0, -20, 0],
      rotate: [0, 10, 0]
    }} transition={{
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-153" />

      <motion.div className="absolute bottom-1/4 -right-4 sm:-right-8 w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-gradient-to-br from-white/10 to-transparent backdrop-blur-xl border border-white/10" animate={{
      y: [0, 20, 0],
      scale: [1, 1.1, 1]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-154" />

      {/* Premium product representation */}
      <div className="absolute inset-0 flex items-center justify-center" data-id="element-155">
        <motion.div className="relative w-36 h-48 sm:w-48 sm:h-64 rounded-2xl bg-gradient-to-b from-white/10 to-white/5 backdrop-blur-2xl border border-white/20 shadow-2xl overflow-hidden" animate={{
        y: [0, -10, 0]
      }} transition={{
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-156">
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent" data-id="element-157" />
          <div className="absolute inset-0 flex items-center justify-center" data-id="element-158">
            <div className="text-center" data-id="element-159">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 rounded-2xl bg-gradient-to-br from-chamaleon-orange to-chamaleon-orange-dark flex items-center justify-center shadow-glow-orange" data-id="element-160">
                <span className="text-white font-bold text-xl sm:text-2xl" data-id="element-161">
                  C
                </span>
              </div>
              <div className="text-white/60 text-xs sm:text-sm font-light tracking-wider" data-id="element-162">
                PREMIUM
              </div>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-chamaleon-orange to-transparent" data-id="element-163" />
        </motion.div>
      </div>

      {/* Orbiting particles */}
      {[...Array(3)].map((_, i) => <motion.div key={i} className="absolute w-2 h-2 rounded-full bg-chamaleon-orange/40" style={{
      top: '50%',
      left: '50%'
    }} animate={{
      x: [0, Math.cos(i * 120 * Math.PI / 180) * 120, 0],
      y: [0, Math.sin(i * 120 * Math.PI / 180) * 120, 0],
      opacity: [0.4, 0.8, 0.4]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      delay: i * 0.3,
      ease: 'easeInOut'
    }} data-id="element-164" />)}
    </div>;
}
export function Hero({
  language
}: HeroProps) {
  const t = content[language];
  return <section className="relative min-h-screen flex items-center overflow-hidden bg-chamaleon-black" data-id="element-165">
      {/* Modern Background Effects */}
      <div className="absolute inset-0" data-id="element-166">
        <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
        backgroundSize: '100px 100px'
      }} data-id="element-167" />
        <div className="absolute inset-0 bg-gradient-to-br from-chamaleon-black via-chamaleon-black to-chamaleon-charcoal" data-id="element-168" />
        <motion.div className="absolute top-1/3 right-1/4 w-[600px] h-[600px] rounded-full opacity-[0.08]" style={{
        background: 'radial-gradient(circle, #FF6B00 0%, transparent 70%)',
        filter: 'blur(100px)'
      }} animate={{
        scale: [1, 1.1, 1],
        opacity: [0.08, 0.12, 0.08]
      }} transition={{
        duration: 8,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-169" />
        <div className="absolute top-1/4 right-0 w-px h-64 bg-gradient-to-b from-transparent via-chamaleon-orange/20 to-transparent" data-id="element-170" />
        <div className="absolute bottom-1/4 left-0 w-px h-64 bg-gradient-to-b from-transparent via-chamaleon-orange/20 to-transparent" data-id="element-171" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40" data-id="element-172">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-173">
          {/* Content - Left Side */}
          <div className="max-w-2xl mx-auto lg:mx-0" data-id="element-174">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-white/5 backdrop-blur-sm rounded-full border border-white/10 mb-6 sm:mb-8" data-id="element-175">
              <div className="w-1.5 h-1.5 rounded-full bg-chamaleon-orange animate-pulse" data-id="element-176" />
              <span className="text-xs sm:text-sm text-gray-400" data-id="element-177">
                Premium Quality Since 1964
              </span>
            </motion.div>

            <motion.h1 initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }} className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-none mb-4 sm:mb-6" data-id="element-178">
              {t.headline}
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }} className="text-xl sm:text-2xl lg:text-3xl text-chamaleon-orange font-light mb-6 sm:mb-8 lg:mb-6" data-id="element-179">
              {t.subheadline}
            </motion.p>

            {/* Mobile Visual - Show after subheadline on mobile only */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1,
            delay: 0.5
          }} className="lg:hidden mb-8 sm:mb-10" data-id="element-180">
              <AbstractVisual data-id="element-181" />
            </motion.div>

            <motion.p initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.5
          }} className="text-base sm:text-lg text-gray-400 mb-8 sm:mb-12 leading-relaxed" data-id="element-182">
              {t.description}
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.6
          }} className="flex flex-col sm:flex-row gap-3 sm:gap-4" data-id="element-183">
              <motion.a href="#products" className="group relative inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-chamaleon-orange text-white font-medium rounded-lg overflow-hidden text-sm sm:text-base" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-184">
                <span className="relative z-10" data-id="element-185">{t.cta1}</span>
                <ArrowRightIcon className="relative z-10 w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-186" />
                <div className="absolute inset-0 bg-gradient-to-r from-chamaleon-orange-dark to-chamaleon-orange opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-187" />
              </motion.a>

              <motion.a href="#partner" className="group inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white/5 backdrop-blur-sm border border-white/10 text-white font-medium rounded-lg hover:bg-white/10 hover:border-chamaleon-orange/30 transition-all duration-300 text-sm sm:text-base" whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} data-id="element-188">
                {t.cta2}
                <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" data-id="element-189" />
              </motion.a>
            </motion.div>
          </div>

          {/* Visual - Right Side - Desktop Only */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1,
          delay: 0.7
        }} className="relative hidden lg:block" data-id="element-190">
            <AbstractVisual data-id="element-191" />
          </motion.div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-chamaleon-black to-transparent pointer-events-none" data-id="element-192" />
    </section>;
}